import { faComputer, faPhone, faTv } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pill } from '@mantine/core';
import type { ReactNode } from 'react';

import { HelpContent, HelpContentHeading } from './HelpContent';

export function UtilityPoleDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Utility pole</h4>
				<Pill.Group>
					<Pill>Power pole</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>A wooden or metal pole that supports power lines, transformers, and other electric system equipment.</p>
		</HelpContent>
	);
}

export function PowerDistributionPrimaryLinesDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Primary lines</h4>
				<Pill.Group>
					<Pill>Power distribution lines</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				Overhead or underground electrical lines that deliver electricity from substations to transformers for
				residential and commercial customers.
			</p>
			<p>Voltage levels: &gt;= 4.8 kV (4,800 volts)</p>
		</HelpContent>
	);
}

export function UtilityTransformerDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Transformer</h4>
			</HelpContentHeading>
			<p>Lowers the high-voltage electricity on power distribution lines to levels a customer's home can use.</p>
		</HelpContent>
	);
}

export function SecondaryLinesDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Secondary lines</h4>
			</HelpContentHeading>
			<p>
				After the transformer reduces voltage, secondary lines deliver usable electricity that runs lights,
				outlets, appliances, and HVAC systems.
			</p>
			<p>Voltage levels: &lt;= 600 V (volts)</p>
		</HelpContent>
	);
}

export function CrossArmDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Cross arm</h4>
			</HelpContentHeading>
			<p>Supports and separates wires to keep them from touching and causing outages or sparks.</p>
		</HelpContent>
	);
}

export function PrimaryInsulatorDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Primary insulator</h4>
			</HelpContentHeading>
			<p>
				The component that high-voltage wires sit on. It prevents electricity from leaking into the pole and
				holds the energized wire safely in place.
			</p>
		</HelpContent>
	);
}

export function ArrestorDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Arrestor</h4>
			</HelpContentHeading>
			<p>
				Protects the electrical system from voltage surges, such as lightning, by diverting extra electricity
				safely to the ground.
			</p>
		</HelpContent>
	);
}

export function PrimaryFuseDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Primary fuse</h4>
			</HelpContentHeading>
			<p>Protects electrical equipment by shutting off power when there is an equipment problem or failure.</p>
		</HelpContent>
	);
}

export function PowerNeutralLineDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Power neutral line</h4>
			</HelpContentHeading>
			<p>
				A current-carrying conductor that operates with primary lines. It is typically a bare wire running along
				the pole, acting as the return path for electricity to help balance the system and keep voltage stable.
			</p>
		</HelpContent>
	);
}

export function PoleGroundDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Pole ground</h4>
			</HelpContentHeading>
			<p>
				A safety wire that runs down the pole into the earth to send extra or dangerous electricity safely into
				the ground.
			</p>
		</HelpContent>
	);
}

export function ServiceDropDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service drop</h4>
				<Pill.Group>
					<Pill>Service line</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>The wire that runs from the pole to the home or building.</p>
			<p>Voltage levels:</p>
			<ul>
				<li>Residential: commonly 240V (single phase)</li>
				<li>Commercial: sometimes 480V (three phase)</li>
			</ul>
		</HelpContent>
	);
}

export function ServicePointDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service point</h4>
			</HelpContentHeading>
			<p>The location where the service drop (service line) connects to the customer's wiring on the house.</p>
		</HelpContent>
	);
}

export function TrainerDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Trainer</h4>
				<Pill.Group>
					<Pill>Cable trainer</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>Equipment that holds cables in place on poles.</p>
		</HelpContent>
	);
}

export function MeterDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Meter</h4>
				<Pill.Group>
					<Pill>Electric meter</Pill>
					<Pill>Smart meter</Pill>
					<Pill>AMI (Advanced Metering Infrastructure) meter</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>The device on a home or building that measures how much electricity is being used.</p>
			<p>
				Smart meters help pinpoint where outages occur, resulting in faster restoration times and improved
				electrical grid safety.
			</p>
		</HelpContent>
	);
}

export function DripLoopDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Drip loop</h4>
			</HelpContentHeading>
			<p>A small downward bend in service wires that keeps rainwater from running into the house.</p>
		</HelpContent>
	);
}

export function WeatherheadDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Weatherhead</h4>
			</HelpContentHeading>
			<p>
				The cap at the top of the pipe on a home where wires enter. It helps keep rain and snow out of the
				electrical system.
			</p>
		</HelpContent>
	);
}

export function ServiceEntranceConductorDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service entrance conductor</h4>
			</HelpContentHeading>
			<p>The wire that carries electricity from the weatherhead into the home's meter and service panel.</p>
		</HelpContent>
	);
}

export function MeterCabinetDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Meter cabinet</h4>
				<Pill.Group>
					<Pill>Meter socket</Pill>
					<Pill>Meter jaws</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>The metal box that holds the electric meter.</p>
			<p>
				The meter jaws are the electrical contacts inside the meter cabinet that connect the meter to the
				service lines and the customer's disconnect switch or service panel.
			</p>
		</HelpContent>
	);
}

export function ServicePanelCircuitBreakerDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service panel</h4>
				<Pill.Group>
					<Pill>Circuit breaker box</Pill>
					<Pill>Fuse box</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				The main electrical distribution panel that contains circuit breakers and routes electricity to circuits
				throughout the home or building.
			</p>
		</HelpContent>
	);
}

export function ServiceMastDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service mast</h4>
				<Pill.Group>
					<Pill>Service riser</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>The metal pipe on a home or building that holds the wires coming from the pole.</p>
		</HelpContent>
	);
}

export function PointOfAttachmentDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Point of attachment</h4>
			</HelpContentHeading>
			<p>
				The hardware on the house where the service drop wire is connected, usually by a hook or bracket near
				the roofline.
			</p>
		</HelpContent>
	);
}

export function CommunicationLinesDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Communication lines</h4>
				<Pill.Group>
					<Pill>Telecommunications lines</Pill>
					<Pill>Cable lines</Pill>
					<Pill>Phone lines</Pill>
					<Pill>Internet lines</Pill>
					<Pill>Foreign (FOR) lines</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>Low voltage telecommmunication lines, typically owned and maintained by other utilities such as:</p>
			<FontAwesomeIcon icon={faComputer} style={{ width: '1.5rem', paddingRight: '0.5rem' }} />
			<b>Internet</b>
			<ul style={{ marginLeft: '2rem', paddingLeft: 0, listStylePosition: 'inside' }}>
				<li>Comcast (Xfinity)</li>
				<li>AT&amp;T</li>
				<li>Wide Open West (WOW!)</li>
			</ul>
			<FontAwesomeIcon icon={faTv} style={{ width: '1.5rem', paddingRight: '0.5rem' }} />
			<b>Cable</b>
			<ul style={{ marginLeft: '2rem', paddingLeft: 0, listStylePosition: 'inside' }}>
				<li>Comcast (Xfinity)</li>
				<li>Charter</li>
			</ul>
			<FontAwesomeIcon icon={faPhone} style={{ width: '1.5rem', paddingRight: '0.5rem' }} />
			<b>Phone</b>
			<ul style={{ marginLeft: '2rem', paddingLeft: 0, listStylePosition: 'inside' }}>
				<li>AT&amp;T</li>
			</ul>
		</HelpContent>
	);
}

export function ServiceMastGuyDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Service mast guy</h4>
			</HelpContentHeading>
			<p>
				A support wire that helps brace and stabilize the service mast where needed, especially under tension
				from the service line.
			</p>
		</HelpContent>
	);
}

export function TreeTrimServiceLineDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Tree trim (service line)</h4>
				<Pill.Group>
					<Pill>Tree trimming around service line</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				Customers are responsible for trimming trees along their service wires and at least 15 feet from DTE
				distribution lines.
			</p>
			<p>DTE can temporarily reroute the service drop so customers can trim safely.</p>
		</HelpContent>
	);
}
